<template>
  <div
    class="wt2-sg wt-bgm-modal"
    :class="{ '-do-not-highlight': doNotHighlight }"
    @mousemove="onMouseMoveOver"
  >
    <div class="-header">
      <div class="-title wt2-text-l3">{{ 'bgMusic.title' | culture }}</div>
      <div v-if="showStoryboardingControls" class="-icon-lock-storyboard-music">
        <i
          class="wticon wt-cursor-pointer"
          title="Lock music selection for this template"
          @click="lockBgMusic = !lockBgMusic"
          :class="{
            'wticon-unlocked': !lockBgMusic,
            'wticon-locked': lockBgMusic,
          }"
        />
      </div>
    </div>
    <div class="-search-controls">
      <wt-search :value="$store.state.filter.query" @input="searchQuery" />
      <div class="-spring">&nbsp;</div>
      <wt-dropdown
        type="normal"
        :items="moodList"
        :num-items="Math.min(7, moodList.length)"
        :current-item-id="$store.state.filter.mood"
        :on-click-item="onChangeMood"
      />
      <wt-toggle
        :label-left="'bgMusic.mine' | culture"
        :checked="$store.state.filter.mine"
        :disabled="false"
        @toggle="onChangeMine"
      />
      <wt-toggle
        :label-left="'bgMusic.favorite' | culture"
        :checked="$store.state.filter.favorite"
        :disabled="false"
        @toggle="onChangeFavorite"
      />
      <wt-button
        :text="'bgMusic.addMore' | culture"
        type="secondary"
        @click="addMore"
      />
    </div>

    <wt-files-dropzone :show="isDragging" ref="fileDrop">
      <template slot="indicator">
        <i class="wticon wticon-upload2" />
        <span class="-drop-text">{{
          'generic.DND_INVITATION_M1' | culture
        }}</span>
        <span class="-drop-formats">{{
          'generic.DND_INVITATION_M2' | culture
        }}</span>
      </template>
    </wt-files-dropzone>

    <div class="-search-result">
      <header-row />

      <div v-if="showLoading" class="-loader">
        <div class="wt-loader" />
      </div>
      <empty-page v-else-if="showEmpty" />
      <template v-else>
        <music-row
          v-if="showSelected"
          :audio="selectedAudio"
          :key="selectedAudio.metadata.assetUUID"
        />
        <wt-continuous-pagination
          ref="list"
          :on-next-page="onNextPage"
          class="-result-set"
        >
          <template v-if="showUploading">
            <music-row
              v-for="value in uploadingResults"
              :key="value.metadata.assetUUID"
              :audio="value"
            />
          </template>

          <transition-group name="list">
            <music-row
              v-for="value in searchResults"
              :key="value.metadata.assetUUID"
              :onFinalClick="onFinalClick"
              :audio="value"
            />
          </transition-group>
        </wt-continuous-pagination>
      </template>
    </div>
    <footer class="-footer">
      <div class="-spring">&nbsp;</div>
      <wt-button
        :text="'bgMusic.closeWindow' | culture"
        type="primary"
        @click="onCloseWindow"
      />
    </footer>
    {{/*invisible element for file choose dialog*/}}
    <input
      v-show="false"
      ref="btnFile"
      type="file"
      multiple="true"
      :accept="acceptedMimes"
      @change="onFileChange"
    />
    {{/*invisible element for audio playback*/}}
    <audio
      v-if="playingValue"
      v-show="false"
      :src="playingValue"
      autoplay
      loop
      @ended="onPlayEnded"
    />
  </div>
</template>

<script>
import app from 'app';
import api from './api';
import Store from './store.vuex';
import EmptyPage from './empty_page.vue';
import HeaderRow from './rowType/header.vue';
import MusicRow from './rowType/music.vue';
import { mapActions, mapState } from 'vuex';

export default {
  components: {
    EmptyPage,
    HeaderRow,
    MusicRow,
  },

  store() {
    return new app.Vuex.Store(Store);
  },

  props: {
    initiallySelected: { type: Object, required: false, default: null },
    options: { type: Object, required: true },
    onToggleSelect: { type: Function, required: true },
    closeModal: { type: Function },
  },

  data() {
    return {
      guid: app.guid('bgmusic-modal'),
      isDragging: false,
      mouse: {
        /** @type {Event} */
        e: null,
        /** @type {number} */
        traveled: 0,
        DONOTHIGHLIGHT_DISTANCE: 3,
      },
      hitCloseButton: false,
      acceptedMimes: app.DEFAULT.EXCLUSIVE_AUDIO_MIMES,
      lockBgMusic: app.storyController.story.get('_lockBgMusic'),
    };
  },

  watch: {
    lockBgMusic(value) {
      if (!this.showStoryboardingControls) {
        return;
      }
      //todo - consolidate with story controller to 1 function in the future
      app.storyController.story.set('_lockBgMusic', value);
      app.api.draftStoryTemplates
        .updateTemplateMetadata({
          storyId: app.storyController.story.get('id'),
          categoryIds: app.storyController.story.get('_categoryIds'),
          lockBgMusic: app.storyController.story.get('_lockBgMusic'),
          channelId: app.rootStore.state.user.channelId,
        })
        .then(() => {
          app.storyController.story.hasBeenChanged();
        })
        .catch((err) => {
          app.doctorController.report(err);
        });
    },
  },

  computed: {
    ...mapState({
      selectedAudio: (state) => state.selected,
    }),

    showStoryboardingControls() {
      return (
        app.rootStore.getters['user/isTemplateSpecialist'] &&
        app.rootStore.state.story.isTemplate
      );
    },

    moodList() {
      const rv = [
        {
          id: '',
          label: app.culture.bgMusic.ddlAll,
        },
      ];
      _.each(this.$store.state.supportedFilters.moods, (mood) => {
        rv.push({ id: mood, label: mood });
      });
      return rv;
    },

    doNotHighlight() {
      return this.mouse.traveled <= this.mouse.DONOTHIGHLIGHT_DISTANCE;
    },

    showEmpty() {
      return (
        !this.$store.state.fetching &&
        _.isEmpty(this.$store.state.gallery) &&
        !this.showUploading
      );
    },

    showLoading() {
      return this.$store.state.hydrating;
    },

    showSelected() {
      return (
        this.selectedAudio &&
        !this.$store.state.filter.mine &&
        !this.$store.state.filter.favorite
      );
    },

    showUploading() {
      return !_.isEmpty(this.$store.state.uploading);
    },

    uploadingResults() {
      // exclude from uploading currently selected
      return this.$store.state.uploading;
    },

    searchResults() {
      // exclude from gallery results currently selected and currently uploaded
      return _.filter(this.$store.state.gallery, (audio) => {
        if (this.showSelected) {
          return api.getId(audio) !== this.$store.getters.selectedId;
        } else {
          return audio;
        }
      });
    },

    playingValue() {
      if (this.$store.state.playing) {
        return app.prependHttp(
          this.$store.state.playing.metadata.hiResAssetUrl
        );
      }
      return null;
    },
  },

  beforeDestroy() {
    app.namespace.Util.FileDndDispatcher.remove(this.guid);
    app.stopListening(
      app.UploadQueue.unfinished,
      'add remove',
      this.$_uploadQueue_changed
    );
    this.DESTROY({ hitCloseButton: this.hitCloseButton });
    this.onToggleSelect({ selected: this.selectedAudio });
    api.$bus.$off();
  },

  created() {
    app.trigger('story:rcp:pause');

    this.INITIALIZE({
      selected: this.initiallySelected,
      options: this.options,
    });

    // prepopulate with currently uploading
    app.UploadQueue.getBGMusicQue().each((uqModel) => {
      this.UNSHIFT_UPLOADING(uqModel.attributes);
    });

    app.listenTo(
      app.UploadQueue.unfinished,
      'add remove',
      this.$_uploadQueue_changed
    );

    api.$bus.$on('ADD_MORE', this.addMore);

    app.namespace.Util.FileDndDispatcher.add({
      guid: this.guid,
      handlers: {
        dragover: this.$_dragOver,
        dragenter: this.$_dragEnter,
        dragleave: this.$_dragLeave,
        drop: this.$_drop,
      },
    });
  },

  methods: {
    ...mapActions([
      'INITIALIZE',
      'DESTROY',
      'CHANGE_FILTER',
      'SEARCH',
      'ADD_MORE',
      'SHIFT_UPLOADING',
      'UNSHIFT_UPLOADING',
      'TOGGLE_PLAYING',
    ]),

    $_uploadQueue_changed(uqModel, uqCollection, options) {
      if (
        uqModel.get('metadata').assetSubType === app.mediaAssetSubType.BG_MUSIC
      ) {
        if (options.add) {
          this.UNSHIFT_UPLOADING(uqModel.attributes);
        } else if (options.remove) {
          this.SHIFT_UPLOADING(uqModel.attributes);
        }
      }
    },

    addMore() {
      this.$refs.btnFile.click();
    },

    searchQuery(value) {
      this.CHANGE_FILTER({ query: value });
    },

    onChangeMood(id /* , item, e*/) {
      this.CHANGE_FILTER({ mood: id });
    },

    onChangeMine(value) {
      this.CHANGE_FILTER({ mine: value });
    },

    onChangeFavorite(value) {
      this.CHANGE_FILTER({ favorite: value });
    },

    onPlayEnded() {
      this.TOGGLE_PLAYING(null);
    },

    onFileChange(e) {
      this.ADD_MORE(e).then(() => {
        this.$nextTick(() => {
          // newly uploaded tracks are at the top level
          if (this.$refs.list) {
            this.$refs.list.$el.scrollTop = 0;
          }
        });
      });
    },

    onFinalClick() {
      this.mouse.traveled = 0;
    },

    onMouseMoveOver(e) {
      // stop accumulating distance after reaching the desired effect
      if (this.doNotHighlight) {
        if (this.mouse.e) {
          this.mouse.traveled += api.distance(e, this.mouse.e);
        }
        this.mouse.e = e;
      }
    },

    onCloseWindow(e) {
      this.hitCloseButton = true;
      this.closeModal(e);
    },

    onNextPage: _.debounce(
      function () {
        this.SEARCH({ abort: false, more: true });
      },
      app.TIME.MENTAL.VISUAL,
      { leading: true, trailing: false, maxWait: app.TIME.MENTAL.VISUAL }
    ),

    $_dragOver(e) {
      const dt = e.dataTransfer;
      if (dt.types && dt.types.indexOf('Files') >= 0) {
        e.preventDefault();
        e.stopPropagation();
        dt.dropEffect = 'copy';
      } else {
        this.isDragging = false;
      }
    },

    $_dragEnter: function (e) {
      e.preventDefault();
      if (e.fromElement === null) {
        this.isDragging = true;
      }
    },

    $_dragLeave: function (e) {
      e.preventDefault();
      if (e.fromElement === null) {
        this.isDragging = false;
      }
    },

    $_drop: function (e) {
      e.preventDefault();
      this.isDragging = false;

      if (this.$refs.fileDrop.active) {
        this.onFileChange(e);
      }
    },
  },
};
</script>
